import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BackButton } from '../../components/BackButton';
import {
  FormPageShell,
  dataTableHeadClass,
} from '../../components/DataTablePanel';
import {
  Badge,
  Button,
  Callout,
  Card,
  ListEmptyState,
  LoadingSpinner,
} from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import type { MydataDocumentDetail } from '../../types/mydata';
import { formatDate, formatDateTime, formatMoney } from '../../utils/format';
import { escapeHtml, printHtmlDocument } from '../../utils/printDocument';

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function asArray(value: unknown): unknown[] {
  if (value == null) return [];
  return Array.isArray(value) ? value : [value];
}

function text(value: unknown, empty: string = t().mydata.emptyValue): string {
  if (value == null || value === '') return empty;
  return String(value);
}

function money(value: unknown): string {
  if (value == null || value === '') return formatMoney(0);
  return formatMoney(String(value));
}

function formatAddress(party: Record<string, unknown> | null): string {
  const labels = t().mydata;
  const address = asRecord(party?.address ?? party?.Address);
  if (!address) return labels.emptyValue;
  return (
    [
      address.street ?? address.Street,
      address.number ?? address.Number,
      address.postalCode ?? address.PostalCode,
      address.city ?? address.City,
    ]
      .filter(Boolean)
      .join(', ') || labels.emptyValue
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] font-medium uppercase tracking-wide text-ink-muted">
        {label}
      </div>
      <div
        className={`mt-1 break-words text-sm text-ink ${
          mono ? 'font-mono text-[13px]' : ''
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function AmountCard({
  label,
  value,
  emphasize,
}: {
  label: string;
  value: string;
  emphasize?: boolean;
}) {
  return (
    <Card
      className={`p-4 ${
        emphasize ? 'border-slate-300 bg-slate-50 dark:border-slate-600 dark:bg-slate-800/50' : ''
      }`}
    >
      <div className="text-[11px] font-medium uppercase tracking-wide text-ink-muted">
        {label}
      </div>
      <div
        className={`mt-2 tabular-nums text-ink ${
          emphasize ? 'text-2xl font-semibold' : 'text-xl font-semibold'
        }`}
      >
        {formatMoney(value)}
      </div>
    </Card>
  );
}

function PartyCard({
  title,
  party,
  highlightVat,
}: {
  title: string;
  party: Record<string, unknown> | null;
  highlightVat?: string | null;
}) {
  const labels = t().mydata;
  const name = text(party?.name ?? party?.Name);
  const vat = text(party?.vatNumber ?? party?.VatNumber);
  const isHighlight =
    highlightVat &&
    vat !== labels.emptyValue &&
    String(highlightVat) === vat;

  return (
    <Card className="h-full p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-ink">{title}</h2>
        {isHighlight ? (
          <Badge variant="info">{labels.vatNumber}</Badge>
        ) : null}
      </div>
      {!party ? (
        <div className="text-sm text-ink-muted">{labels.emptyValue}</div>
      ) : (
        <div className="space-y-3">
          <div>
            <div className="text-base font-semibold text-ink">{name}</div>
            <div className="mt-1 font-mono text-sm text-ink-muted">{vat}</div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label={labels.country}
              value={text(party.country ?? party.Country)}
            />
            <Field
              label={labels.branch}
              value={text(party.branch ?? party.Branch)}
            />
            <div className="sm:col-span-2">
              <Field label={labels.address} value={formatAddress(party)} />
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

function classificationText(line: Record<string, unknown>): string {
  const income = asArray(
    line.incomeClassification ?? line.IncomeClassification,
  );
  const expenses = asArray(
    line.expensesClassification ?? line.ExpensesClassification,
  );
  const parts: string[] = [];
  for (const item of [...income, ...expenses]) {
    const row = asRecord(item);
    if (!row) continue;
    const cat = text(
      row.classificationCategory ?? row.ClassificationCategory,
      '',
    );
    const type = text(row.classificationType ?? row.ClassificationType, '');
    const amount = row.amount ?? row.Amount;
    const chunk = [cat, type].filter(Boolean).join(' / ');
    if (chunk) {
      parts.push(
        amount != null ? `${chunk} (${formatMoney(String(amount))})` : chunk,
      );
    }
  }
  return parts.join('; ') || t().mydata.emptyValue;
}

function partyPrintBlock(
  title: string,
  party: Record<string, unknown> | null,
): string {
  const labels = t().mydata;
  if (!party) {
    return `<div class="summary-card"><div class="label">${escapeHtml(title)}</div><div>${escapeHtml(labels.emptyValue)}</div></div>`;
  }
  const name = text(party.name ?? party.Name);
  const vat = text(party.vatNumber ?? party.VatNumber);
  return `<div class="summary-card"><div class="label">${escapeHtml(title)}</div><div>${escapeHtml(name)}</div><div>ΑΦΜ: ${escapeHtml(vat)}</div></div>`;
}

function printMydataCard(
  doc: MydataDocumentDetail,
  lines: Record<string, unknown>[],
  issuer: Record<string, unknown> | null,
  counterpart: Record<string, unknown> | null,
) {
  const labels = t().mydata;
  const title = `${labels.documentTitle} ${doc.mark}`;
  const direction =
    doc.direction === 'transmitted'
      ? labels.directionTransmitted
      : labels.directionReceived;
  const seriesAa =
    [doc.series, doc.aa].filter(Boolean).join(' / ') || labels.emptyValue;

  const lineRows = lines
    .map((line, index) => {
      const no = text(line.lineNumber ?? line.LineNumber, String(index + 1));
      const desc = text(
        line.itemDescr ?? line.ItemDescr ?? line.lineComments,
      );
      const net = money(line.netValue ?? line.NetValue);
      const vatCat = text(line.vatCategory ?? line.VatCategory);
      const vat = money(line.vatAmount ?? line.VatAmount);
      const cls = classificationText(line);
      return `<tr>
        <td>${escapeHtml(no)}</td>
        <td>${escapeHtml(desc)}</td>
        <td class="num">${escapeHtml(net)}</td>
        <td>${escapeHtml(vatCat)}</td>
        <td class="num">${escapeHtml(vat)}</td>
        <td>${escapeHtml(cls)}</td>
      </tr>`;
    })
    .join('');

  const html = `
    <div class="sheet">
      <h1>${escapeHtml(title)}</h1>
      <div class="meta">
        MARK: ${escapeHtml(doc.mark)} · ${escapeHtml(labels.issueDate)}: ${escapeHtml(formatDate(doc.issueDate))} · ${escapeHtml(direction)} · ${escapeHtml(labels.seriesAa)}: ${escapeHtml(seriesAa)}
      </div>
      <div class="summary">
        ${partyPrintBlock(labels.issuer, issuer)}
        ${partyPrintBlock(labels.counterpart, counterpart)}
        <div class="summary-card">
          <div class="label">${escapeHtml(labels.totals)}</div>
          <div class="row"><span>${escapeHtml(labels.net)}</span><span>${escapeHtml(formatMoney(doc.netValue))}</span></div>
          <div class="row"><span>${escapeHtml(labels.vat)}</span><span>${escapeHtml(formatMoney(doc.vatAmount))}</span></div>
          <div class="row"><span>${escapeHtml(labels.gross)}</span><span>${escapeHtml(formatMoney(doc.grossValue))}</span></div>
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th>${escapeHtml(labels.lineNo)}</th>
            <th>${escapeHtml(labels.description)}</th>
            <th class="num">${escapeHtml(labels.net)}</th>
            <th>${escapeHtml(labels.vatCategory)}</th>
            <th class="num">${escapeHtml(labels.vat)}</th>
            <th>${escapeHtml(labels.classification)}</th>
          </tr>
        </thead>
        <tbody>${lineRows || `<tr><td colspan="6">${escapeHtml(labels.emptyValue)}</td></tr>`}</tbody>
      </table>
    </div>
  `;
  printHtmlDocument(title, html);
}

export function MydataDocumentPage() {
  const labels = t().mydata;
  const { mark: markParam } = useParams<{ mark: string }>();
  const mark = markParam ? decodeURIComponent(markParam) : '';
  const [doc, setDoc] = useState<MydataDocumentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfInfo, setPdfInfo] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!mark) return;
      setLoading(true);
      setError(null);
      try {
        const data = await api.getMydataDocument(mark);
        if (!cancelled) setDoc(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [mark]);

  const payload = doc?.payload ?? {};
  const header = asRecord(payload.invoiceHeader);
  const issuer = asRecord(payload.issuer);
  const counterpart = asRecord(payload.counterpart);
  const lines = useMemo(
    () =>
      asArray(payload.invoiceDetails)
        .map(asRecord)
        .filter(Boolean) as Record<string, unknown>[],
    [payload.invoiceDetails],
  );
  const payments = useMemo(
    () =>
      asArray(payload.paymentMethods)
        .flatMap((item) => {
          const row = asRecord(item);
          if (!row) return [];
          if (row.paymentMethodDetails || row.PaymentMethodDetails) {
            return asArray(
              row.paymentMethodDetails ?? row.PaymentMethodDetails,
            );
          }
          return [row];
        })
        .map(asRecord)
        .filter(Boolean) as Record<string, unknown>[],
    [payload.paymentMethods],
  );

  async function handleDownloadPdf() {
    if (!doc) return;
    setPdfLoading(true);
    setPdfError(null);
    setPdfInfo(null);
    const previewWindow = window.open('about:blank', '_blank');
    try {
      const series = doc.series ?? 'doc';
      const aa = doc.aa ?? doc.mark;
      const result = await api.downloadMydataDocumentPdf(
        doc.mark,
        `mydata-${series}-${aa}-${doc.mark}.pdf`,
        { previewWindow },
      );
      if (result.mode === 'html-viewer') {
        setPdfInfo(labels.pdfHtmlViewerOpened);
      }
    } catch (err) {
      previewWindow?.close();
      setPdfError(
        err instanceof ApiError ? err.message : labels.pdfUnavailable,
      );
    } finally {
      setPdfLoading(false);
    }
  }

  if (loading) {
    return <LoadingSpinner message={t().common.loading} />;
  }

  if (error || !doc) {
    return (
      <FormPageShell wide>
        <Callout variant="danger">{error || t().common.error}</Callout>
        <Link
          to="/mydata"
          className="text-sm font-medium text-brand hover:underline"
        >
          {labels.backToPeriod}
        </Link>
      </FormPageShell>
    );
  }

  const qrUrl = doc.qrCodeUrl || text(payload.qrCodeUrl, '');
  const hasOfficialPdf =
    doc.hasOfficialPdf ||
    !!doc.downloadingInvoiceUrl ||
    !!text(
      payload.downloadingInvoiceUrl ??
        payload.DownloadingInvoiceUrl ??
        payload.downloadingIncoiceUrl,
      '',
    );

  const seriesAa =
    [doc.series, doc.aa].filter(Boolean).join(' / ') || labels.emptyValue;
  const directionLabel =
    doc.direction === 'transmitted'
      ? labels.directionTransmitted
      : labels.directionReceived;
  const invoiceType = text(doc.invoiceType ?? header?.invoiceType);
  const ownVatHint =
    doc.direction === 'transmitted'
      ? text(issuer?.vatNumber ?? issuer?.VatNumber, '')
      : text(counterpart?.vatNumber ?? counterpart?.VatNumber, '');

  return (
    <FormPageShell
      wide
      contentClassName="space-y-5 pr-2 sm:pr-3"
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <BackButton fallback="/mydata" />
          <div className="flex flex-wrap gap-2">
            {qrUrl ? (
              <a
                href={qrUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-8 items-center rounded-lg border border-line px-3 text-xs font-medium text-ink hover:bg-surface-muted"
              >
                {labels.openQr}
              </a>
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => printMydataCard(doc, lines, issuer, counterpart)}
            >
              {labels.printCard}
            </Button>
            {hasOfficialPdf ? (
              <Button
                type="button"
                size="sm"
                loading={pdfLoading}
                onClick={() => void handleDownloadPdf()}
              >
                {pdfLoading ? labels.downloadingPdf : labels.downloadPdf}
              </Button>
            ) : null}
          </div>
        </div>
      }
    >
      <Card className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xs font-medium uppercase tracking-wide text-ink-muted">
              {labels.documentTitle}
            </div>
            <h1 className="mt-1 text-xl font-semibold text-ink">
              {seriesAa !== labels.emptyValue
                ? `${labels.seriesAa}: ${seriesAa}`
                : `${labels.mark} ${doc.mark}`}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-ink-muted">
              <span>{formatDate(doc.issueDate)}</span>
              <span aria-hidden>·</span>
              <span>{invoiceType}</span>
              <span aria-hidden>·</span>
              <span className="font-mono text-[13px] text-ink">{doc.mark}</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Badge
                variant={
                  doc.direction === 'transmitted' ? 'success' : 'secondary'
                }
              >
                {directionLabel}
              </Badge>
              {doc.cancelledByMark ? (
                <Badge variant="danger">
                  {labels.cancelled}: {doc.cancelledByMark}
                </Badge>
              ) : null}
              {text(header?.currency ?? header?.Currency, '') ? (
                <Badge variant="secondary">
                  {labels.currency}:{' '}
                  {text(header?.currency ?? header?.Currency)}
                </Badge>
              ) : null}
            </div>
          </div>
          <div className="rounded-lg border border-line bg-surface-muted/60 px-4 py-3 text-right">
            <div className="text-[11px] font-medium uppercase tracking-wide text-ink-muted">
              {labels.gross}
            </div>
            <div className="mt-1 text-2xl font-semibold tabular-nums text-ink">
              {formatMoney(doc.grossValue)}
            </div>
          </div>
        </div>
      </Card>

      {pdfError ? <Callout variant="danger">{pdfError}</Callout> : null}
      {pdfInfo ? <Callout variant="info">{pdfInfo}</Callout> : null}
      {!hasOfficialPdf ? (
        <Callout variant="info">{labels.pdfUnavailable}</Callout>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-3">
        <AmountCard label={labels.net} value={doc.netValue} />
        <AmountCard label={labels.vat} value={doc.vatAmount} />
        <AmountCard label={labels.gross} value={doc.grossValue} emphasize />
      </div>

      <div>
        <h2 className="mb-3 text-sm font-semibold text-ink">{labels.parties}</h2>
        <div className="grid gap-3 lg:grid-cols-2">
          <PartyCard
            title={labels.issuer}
            party={issuer}
            highlightVat={
              doc.direction === 'transmitted' ? ownVatHint || null : null
            }
          />
          <PartyCard
            title={labels.counterpart}
            party={counterpart}
            highlightVat={
              doc.direction === 'received' ? ownVatHint || null : null
            }
          />
        </div>
      </div>

      <Card className="p-4">
        <h2 className="mb-3 text-sm font-semibold text-ink">{labels.identity}</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label={labels.mark} value={doc.mark} mono />
          <Field label={labels.uid} value={text(doc.uid)} mono />
          <Field label={labels.issueDate} value={formatDate(doc.issueDate)} />
          <Field label={labels.invoiceType} value={invoiceType} />
          <Field label={labels.seriesAa} value={seriesAa} />
          <Field label={labels.direction} value={directionLabel} />
          <Field
            label={labels.currency}
            value={text(header?.currency ?? header?.Currency)}
          />
          <Field
            label={labels.syncedAt}
            value={
              doc.syncedAt ? formatDateTime(doc.syncedAt) : labels.emptyValue
            }
          />
          {doc.cancelledByMark ? (
            <Field
              label={labels.cancelledByMark}
              value={
                <Badge variant="danger">{doc.cancelledByMark}</Badge>
              }
            />
          ) : null}
          {qrUrl ? (
            <Field
              label={labels.qrCode}
              value={
                <a
                  href={qrUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-brand hover:underline"
                >
                  {labels.openQr}
                </a>
              }
            />
          ) : null}
        </div>
      </Card>

      <Card className="p-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-ink">{labels.lines}</h2>
          <span className="text-xs text-ink-muted">
            {lines.length} {labels.lines.toLowerCase()}
          </span>
        </div>
        {lines.length === 0 ? (
          <ListEmptyState title={labels.noLines} />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-line">
            <table className="min-w-full text-left text-sm">
              <thead className={dataTableHeadClass}>
                <tr>
                  <th className="px-3 py-2">{labels.lineNo}</th>
                  <th className="px-3 py-2">{labels.description}</th>
                  <th className="px-3 py-2 text-right">{labels.net}</th>
                  <th className="px-3 py-2">{labels.vatCategory}</th>
                  <th className="px-3 py-2 text-right">{labels.vat}</th>
                  <th className="px-3 py-2">{labels.classification}</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, index) => (
                  <tr
                    key={String(line.lineNumber ?? line.LineNumber ?? index)}
                    className="border-t border-line"
                  >
                    <td className="whitespace-nowrap px-3 py-2.5 text-ink-muted">
                      {text(
                        line.lineNumber ?? line.LineNumber,
                        String(index + 1),
                      )}
                    </td>
                    <td className="max-w-xs px-3 py-2.5 text-ink">
                      {text(
                        line.itemDescr ?? line.ItemDescr ?? line.lineComments,
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-ink">
                      {money(line.netValue ?? line.NetValue)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-ink-muted">
                      {text(line.vatCategory ?? line.VatCategory)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-ink">
                      {money(line.vatAmount ?? line.VatAmount)}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-ink-muted">
                      {classificationText(line)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="p-4">
        <h2 className="mb-3 text-sm font-semibold text-ink">{labels.payments}</h2>
        {payments.length === 0 ? (
          <div className="text-sm text-ink-muted">{labels.noPayments}</div>
        ) : (
          <ul className="divide-y divide-line rounded-lg border border-line">
            {payments.map((payment, index) => (
              <li
                key={index}
                className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm"
              >
                <span className="text-ink-muted">
                  {labels.paymentType}{' '}
                  {text(
                    payment.type ??
                      payment.Type ??
                      payment.paymentMethodType,
                  )}
                </span>
                <span className="font-medium tabular-nums text-ink">
                  {money(payment.amount ?? payment.Amount)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </FormPageShell>
  );
}
